import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAV, scrollToId } from '../../data/content';

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const left = NAV.links.slice(0, 2);
  const right = NAV.links.slice(2);

  const linkCls =
    'link-underline text-[11px] md:text-xs tracking-[0.22em] uppercase text-teal font-medium';

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open ? 'bg-cream/80 backdrop-blur-xl border-b border-teal/10' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1500px] mx-auto px-6 md:px-12 h-[68px] md:h-[72px] grid grid-cols-[1fr_auto_1fr] items-center">
        {/* Desktop left links */}
        <div className="hidden md:flex items-center gap-8">
          {left.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-${l.target}`}
              onClick={() => scrollToId(l.target)}
              className={linkCls}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Mobile menu trigger */}
        <button
          data-testid="nav-menu-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((o) => !o)}
          className="md:hidden justify-self-start text-teal p-1"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>

        {/* Logo */}
        <button
          onClick={() => {
            setOpen(false);
            scrollToId('top');
          }}
          data-testid="nav-logo"
          className="justify-self-center"
          aria-label="The Unscene Cult"
        >
          <img
            src="/assets/logo-teal.png"
            alt="The Unscene Cult"
            className="h-6 md:h-8 w-auto"
          />
        </button>

        {/* Desktop right links */}
        <div className="hidden md:flex items-center gap-8 justify-end">
          {right.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-${l.target}`}
              onClick={() => scrollToId(l.target)}
              className={linkCls}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Mobile spacer keeps the logo centred */}
        <div className="md:hidden w-[30px] justify-self-end" />
      </nav>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-cream/95 backdrop-blur-xl border-b border-teal/10"
        >
          <div className="px-6 py-6 flex flex-col items-start gap-5">
            {NAV.links.map((l) => (
              <button
                key={l.target}
                data-testid={`mobile-nav-${l.target}`}
                onClick={() => {
                  setOpen(false);
                  scrollToId(l.target);
                }}
                className="text-sm tracking-[0.24em] uppercase text-teal font-medium"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.header>
  );
};
